import { useEffect, useState } from 'react';
import { Box, Card, Heading, Icon, Text, TextButton } from '@zillow/constellation-10';
import { IconArrowRightFilled } from '@zillow/constellation-icons';
import type { YourRentalRewardsCardDto, YourRentalRewardsUserDto } from '@zg-rentals/ts-your-rental-api-client';
import CoinIcon from './CoinIcon';

type RewardsSummaryCardProps = {
  rewardsCard: YourRentalRewardsCardDto;
  user: YourRentalRewardsUserDto;
  maxPoints: number;
};

export default function RewardsSummaryCard({ rewardsCard, user, maxPoints }: RewardsSummaryCardProps) {
  const { cardTitle, pointsValueDescription, ctas } = rewardsCard;
  const points = user.currentPointBalance ? parseInt(user.currentPointBalance, 10) || 0 : 0;
  const clampedPoints = Math.max(points, 0);
  const percentage = maxPoints > 0 ? (clampedPoints / maxPoints) * 100 : 0;
  const cardCta = ctas?.[0];

  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    if (percentage > 0) {
      const frame = requestAnimationFrame(() => setAnimated(true));
      return () => cancelAnimationFrame(frame);
    }
  }, [percentage]);

  return (
    <Card
      elevated={false}
      css={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 'loose',
        gap: 'default',
      }}
      data-testid="RewardsSummaryCard"
    >
      <Heading level={3} textStyle="heading-xs">
        {cardTitle}
      </Heading>
      <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
        <CoinIcon size={40} />
        <Text textStyle="heading-md">
          <strong>{clampedPoints.toLocaleString()}</strong> pts
        </Text>
      </Box>
      {pointsValueDescription && <Text textStyle="body-sm">{pointsValueDescription}</Text>}
      <Box
        role="progressbar"
        aria-valuenow={Math.min(clampedPoints, maxPoints)}
        aria-valuemin={0}
        aria-valuemax={maxPoints}
        aria-label={`${clampedPoints.toLocaleString()} of ${maxPoints.toLocaleString()} reward points earned`}
        css={{
          position: 'relative',
          width: '100%',
          height: '16px',
          backgroundColor: 'bg.softer',
          borderRadius: 'capsule',
          overflow: 'hidden',
        }}
        data-testid="RewardsSummaryCard-ProgressBar"
      >
        <Box
          css={{
            position: 'absolute',
            top: '0',
            left: '0',
            height: '100%',
            minWidth: animated && percentage > 0 ? '16px' : '0',
            transition: 'width 0.8s ease-out',
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
            },
            background:
              'linear-gradient(to right, token(colors.bg.express.inspire.hero), token(colors.bg.express.inspire.impact))',
          }}
          style={{
            width: animated && percentage > 0 ? `${percentage}%` : '0',
          }}
        />
      </Box>
      {cardCta && (
        <TextButton textStyle="body-bold" tone="brand" asChild>
          <a href={cardCta.url} data-testid="RewardsSummaryCard-Cta">
            {cardCta.text}
            <Icon render={<IconArrowRightFilled />} css={{ marginLeft: 'tighter' }} />
          </a>
        </TextButton>
      )}
    </Card>
  );
}
